import {
  type BridgeSeat,
  COLOUR_DASH_TARGET,
  type ColourDashGame,
  type ColourDashResult,
  type ColourDashRobotLevel,
  type ColourDashTotals,
  type DashCard,
  type DashColour,
  SINGLE_PLAYER,
  addColourDashHand,
  colourDashCallLast,
  colourDashChoose,
  colourDashDraw,
  colourDashGameWinner,
  colourDashLegalPlays,
  colourDashPass,
  colourDashPlay,
  colourDashResult,
  colourDashViewFor,
  isDashWild,
  newColourDashGame,
  seededRandom,
} from '@chessmate/game-engine';
import { create } from 'zustand';
import { seatPan } from '@chessmate/audio';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';
import { cueDeal, cueRoundOrGame, cueTurn } from './sfx-cues.js';
import { chatGameEnd, chatRound, chatterNewGame, robotsOf } from './table-chatter.js';

/**
 * The Colour Dash store (single device: you in South, robots in the other seats). Its own store, like Hearts' and Crazy Eights', because nothing
 * here is FEN-shaped. Robots decide from `colourDashViewFor` only.
 *
 * A game is a run of hands; the winner of a hand scores the card points left in the others' hands and the first to 500 wins the game.
 * Points are only a score: nothing is staked.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';
export type PlayerCount = 2 | 3 | 4;
export const TABLE_SEATS: Readonly<Record<PlayerCount, readonly BridgeSeat[]>> = {
  2: ['N', 'S'],
  3: ['N', 'S', 'W'],
  4: ['N', 'E', 'S', 'W'],
};

/** Pause before a robot acts so a kid can follow the pile. Zero in tests and with `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export interface ColourDashState {
  game: ColourDashGame | null;
  result: ColourDashResult | null;
  /** Hands dealt so far in this game, counting the current one (0 = no game). */
  handNumber: number;
  totals: ColourDashTotals;
  /** The score that wins the game: `COLOUR_DASH_TARGET`, or `?target=` when the page was opened with one (tests and audits). */
  target: number;
  gameWinner: BridgeSeat | null;
  players: PlayerCount;
  strength: ColourDashRobotLevel;
  seed: number | null;
  query: AppQuery | null;
  /** A wild the human has chosen to play and now has to name a colour for. */
  pendingWild: DashCard | null;
  start(query: AppQuery, seed?: number): void;
  nextHand(): void;
  newGame(): void;
  stop(): void;
  /** Tap a card: a wild asks for a colour first; any other legal card is played. */
  tap(card: DashCard): void;
  chooseColour(colour: DashColour): void;
  cancelWild(): void;
  draw(): void;
  pass(): void;
  /** Say "last card" (allowed while you hold two cards on your turn). */
  callLast(): void;
  setStrength(strength: ColourDashRobotLevel): void;
  setPlayers(players: PlayerCount): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each hand of a game has its own seed derived from the game's, so a seeded game replays exactly. */
export const handSeed = (seed: number, handIndex: number): number => (seed + Math.imul(handIndex, 7919)) >>> 0;

export const useColourDashStore = create<ColourDashState>((set, get) => {
  /** Take a new game state; when it ends the hand, score it, and when that ends the game, say so. */
  const advance = (game: ColourDashGame) => {
    const before = get().game;
    const result = colourDashResult(game);
    if (!result) {
      set({ game });
      cueTurn(before && before.phase === 'play' ? before.turn : null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
      return;
    }
    const totals = addColourDashHand(get().totals, result);
    const winner = colourDashGameWinner(totals, get().target);
    set({ game, result, totals, gameWinner: winner, pendingWild: null });
    // The game-ending hand gets win/lose; any other hand the stinger.
    cueRoundOrGame((winner ?? result.winner) === HUMAN_SEAT ? 'won' : 'lost', winner !== null);
    // Table talk (public: who went out, who won the game).
    const robots = robotsOf(TABLE_SEATS[get().players]);
    if (winner) chatGameEnd([winner], robots);
    else chatRound(robots.includes(result.winner) ? result.winner : null, robots.find((r) => r !== result.winner) ?? null);
  };

  /** If a robot is to act, let it, after a short pause; stops at the human's turn or the end of the hand. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done' || game.controllers[game.turn] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase === 'done' || current.controllers[current.turn] !== 'ai') return;
      const random = seededRandom(current.seed + current.discard.length * 31 + current.stock.length);
      const action = colourDashChoose(colourDashViewFor(current, current.turn), strength, random);
      if (action.type === 'play') {
        // The card is heard from the seat that played it (spec P2: seat panning).
        playSfx('card-play', seatPan(current.turn));
        advance(colourDashPlay(current, action.card, action.colour));
      } else if (action.type === 'draw') advance(colourDashDraw(current));
      else if (action.type === 'call-last') advance(colourDashCallLast(current));
      else advance(colourDashPass(current));
      driveRobots();
    }, delay);
  };

  const deal = (handIndex: number) => {
    clearTimer();
    const { seed, players } = get();
    if (seed === null) return;
    const seats = TABLE_SEATS[players];
    const game = newColourDashGame({ seed: handSeed(seed, handIndex), seats: [...seats], dealer: seats[handIndex % seats.length], controllers: SINGLE_PLAYER });
    set({ game, result: null, handNumber: handIndex + 1, pendingWild: null });
    cueDeal();
    cueTurn(null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
    driveRobots();
  };

  const begin = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    set({ seed, query, target: query.target ?? COLOUR_DASH_TARGET, totals: {}, gameWinner: null, result: null, handNumber: 0, pendingWild: null });
    deal(0);
  };

  const humanTurn = (g: ColourDashGame | null): g is ColourDashGame => !!g && g.phase === 'play' && g.turn === HUMAN_SEAT;

  return {
    game: null,
    result: null,
    handNumber: 0,
    totals: {},
    target: COLOUR_DASH_TARGET,
    gameWinner: null,
    players: 4,
    strength: 'easy',
    seed: null,
    query: null,
    pendingWild: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      return begin(query, seed ?? query.deal ?? freshSeed());
    },
    nextHand: () => {
      const { result, gameWinner: winner, handNumber } = get();
      if (!result || winner) return;
      deal(handNumber);
    },
    newGame: () => {
      const { query } = get();
      if (query) begin(query, query.deal ?? freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null, handNumber: 0, totals: {}, target: COLOUR_DASH_TARGET, gameWinner: null, pendingWild: null });
    },
    tap: (card) => {
      const { game } = get();
      if (!humanTurn(game) || !colourDashLegalPlays(game).some((c) => c.id === card.id)) return;
      if (isDashWild(card)) {
        set({ pendingWild: card });
        return;
      }
      // Only a card that reaches the table is heard; an illegal tap is silent.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      advance(colourDashPlay(game, card));
      driveRobots();
    },
    chooseColour: (colour) => {
      const { game, pendingWild } = get();
      if (!humanTurn(game) || !pendingWild) return;
      set({ pendingWild: null });
      // The wild card reaches the table now, so this is where it is heard.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      advance(colourDashPlay(game, pendingWild, colour));
      driveRobots();
    },
    cancelWild: () => set({ pendingWild: null }),
    draw: () => {
      const { game } = get();
      if (!humanTurn(game)) return;
      if (!colourDashViewFor(game, HUMAN_SEAT).canDraw) return;
      advance(colourDashDraw(game));
      driveRobots();
    },
    pass: () => {
      const { game } = get();
      if (!humanTurn(game) || !game.drew) return;
      advance(colourDashPass(game));
      driveRobots();
    },
    callLast: () => {
      const { game } = get();
      if (!humanTurn(game) || !colourDashViewFor(game, HUMAN_SEAT).canCallLast) return;
      advance(colourDashCallLast(game));
    },
    setStrength: (strength) => set({ strength }),
    setPlayers: (players) => {
      set({ players });
      const { query } = get();
      if (query && get().seed !== null) begin(query, get().seed as number);
    },
  };
});

export { COLOUR_DASH_TARGET };
